import React, { useState, useMemo } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  FolderKanban,
} from 'lucide-react';
import {
  useTaskingBotTasks,
  type TaskingBotTask,
} from '../../helpers/useTaskingBotTasks';
import { useTaskingBotProjects, type TaskingBotProject } from '../../helpers/useTaskingBotProjects';
import { TaskDetailModal } from '../TaskDetailModal';
import styles from './TaskSchedule.module.css';

// ─── Constants ─────────────────────────────────────────────
const STATUS_BADGE: Record<string, { label: string; color: string; bg: string }> = {
  open: { label: 'To Do', color: '#9ca3af', bg: 'rgba(156,163,175,0.12)' },
  in_progress: { label: 'In Progress', color: '#f59e0b', bg: 'rgba(245,158,11,0.12)' },
  completed: { label: 'Done', color: '#22c55e', bg: 'rgba(34,197,94,0.12)' },
  running: { label: 'Running', color: '#3b82f6', bg: 'rgba(59,130,246,0.12)' },
  waiting_for_input: { label: 'Blocked', color: '#ef4444', bg: 'rgba(239,68,68,0.12)' },
};

const PRIORITY_COLORS: Record<string, string> = {
  urgent: '#ef4444', high: '#f97316', medium: '#f59e0b', low: '#6b7280',
};
const PRIORITY_LABELS: Record<string, string> = {
  urgent: 'Urgent', high: 'High', medium: 'Medium', low: 'Low',
};

const PROJECT_PALETTE = ['#6366f1', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316', '#06b6d4', '#84cc16'];

function getProjectColor(project: TaskingBotProject | undefined): string {
  if (!project) return '#6b7280';
  if (project.color) return project.color;
  let hash = 0;
  for (let i = 0; i < project.id.length; i++) hash = (hash * 31 + project.id.charCodeAt(i)) | 0;
  return PROJECT_PALETTE[Math.abs(hash) % PROJECT_PALETTE.length];
}

// ─── Helpers ───────────────────────────────────────────────
type ScheduleRange = 'day' | 'week' | 'month';

function startOfWeek(d: Date): Date {
  const day = d.getDay();
  const diff = d.getDate() - day;
  return new Date(d.getFullYear(), d.getMonth(), diff);
}

function addDays(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
}

function formatDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function getTaskDate(task: TaskingBotTask): string | null {
  return task.dueDate || task.endDate || task.startDate || task.createdAt || null;
}

/** Parse cron field into a set of allowed values */
function parseCronField(field: string, min: number, max: number): number[] | null {
  if (field === '*') return null; // any value
  const values = new Set<number>();
  for (const part of field.split(',')) {
    if (part.includes('/')) {
      // Step: */3 or 1/2
      const [base, step] = part.split('/');
      const s = Number(step);
      const start = base === '*' ? min : Number(base);
      for (let i = start; i <= max; i += s) values.add(i);
    } else if (part.includes('-')) {
      // Range: 1-5
      const [a, b] = part.split('-').map(Number);
      for (let i = a; i <= b; i++) values.add(i);
    } else {
      values.add(Number(part));
    }
  }
  return values.size > 0 ? Array.from(values) : null;
}

/** Parse a cron expression and generate occurrence dates within a range */
/** Extract time-of-day from a cron expression "M H * * *" → "9:00 AM" */
function getCronTime(cronExpr: string | null | undefined): string | null {
  if (!cronExpr) return null;
  const parts = cronExpr.trim().split(/\s+/);
  if (parts.length < 2) return null;
  const minPart = parts[0];
  const hourPart = parts[1];
  // Only show time if minute and hour are concrete (not * or */N)
  if (minPart === '*' || hourPart === '*' || minPart.includes('/') || hourPart.includes('/')) return null;
  // Take the first value if comma-separated
  const min = parseInt(minPart.split(',')[0], 10);
  const hour = parseInt(hourPart.split(',')[0], 10);
  if (isNaN(min) || isNaN(hour)) return null;
  const period = hour >= 12 ? 'PM' : 'AM';
  const h12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${h12}:${String(min).padStart(2, '0')} ${period}`;
}

/** Get task display time: cron time, dueTime, or startTime */
function getTaskTime(task: TaskingBotTask): string | null {
  if (task.cronExpression) return getCronTime(task.cronExpression);
  const t = (task as any).dueTime || (task as any).startTime;
  if (!t) return null;
  // dueTime is HH:MM format
  const [h, m] = String(t).split(':').map(Number);
  if (isNaN(h)) return null;
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${String(m || 0).padStart(2, '0')} ${period}`;
}

function getCronDates(cronExpr: string, rangeStart: Date, rangeEnd: Date): string[] {
  if (!cronExpr) return [];
  const parts = cronExpr.trim().split(/\s+/);
  if (parts.length < 5) return [];
  const [, , domPart, monthPart, dowPart] = parts;

  const allowedDom = parseCronField(domPart, 1, 31);
  const allowedMonth = parseCronField(monthPart, 1, 12);
  const allowedDow = parseCronField(dowPart, 0, 6);

  const dates: string[] = [];
  const d = new Date(rangeStart);
  while (d <= rangeEnd) {
    const dom = d.getDate();
    const month = d.getMonth() + 1; // JS months are 0-based
    const dow = d.getDay();

    const matchDom = !allowedDom || allowedDom.includes(dom);
    const matchMonth = !allowedMonth || allowedMonth.includes(month);
    const matchDow = !allowedDow || allowedDow.includes(dow);

    // Standard cron: if both DOM and DOW are specified (not *), match either.
    // If only one is specified, match that one.
    const domSpecified = domPart !== '*';
    const dowSpecified = dowPart !== '*';

    let matches: boolean;
    if (domSpecified && dowSpecified) {
      matches = matchMonth && (matchDom || matchDow);
    } else {
      matches = matchMonth && matchDom && matchDow;
    }

    if (matches) {
      dates.push(formatDate(d));
    }
    d.setDate(d.getDate() + 1);
  }
  return dates;
}

// ─── ProjectTag ────────────────────────────────────────────
function ProjectTag({ project, size = 'sm' }: { project?: TaskingBotProject; size?: 'sm' | 'xs' }) {
  if (!project) return null;
  const color = getProjectColor(project);
  const fontSize = size === 'xs' ? '9px' : '10px';
  const padding = size === 'xs' ? '1px 5px' : '2px 7px';
  return (
    <span className={styles.projectTag} style={{ color, background: `${color}14`, borderColor: `${color}30`, fontSize, padding }}>
      <FolderKanban size={size === 'xs' ? 8 : 9} />
      {project.name}
    </span>
  );
}

// ─── Main Component ────────────────────────────────────────
export function TaskSchedule({ projectId }: { projectId?: string | null }) {
  const filters: { projectId?: string; limit: number } = { limit: 500 };
  if (projectId) filters.projectId = projectId;
  const { data: tasks, isLoading } = useTaskingBotTasks(filters);
  const { data: projects } = useTaskingBotProjects();
  const [detailTaskId, setDetailTaskId] = useState<string | null>(null);
  const [range, setRange] = useState<ScheduleRange>('week');
  const [anchor, setAnchor] = useState(() => new Date());

  const projectMap = useMemo(() => {
    const map = new Map<string, TaskingBotProject>();
    (projects || []).forEach(p => map.set(p.id, p));
    return map;
  }, [projects]);

  const allTasks = tasks || [];

  const today = new Date();
  const todayStr = formatDate(today);

  // Build date range
  const dates: Date[] = useMemo(() => {
    if (range === 'day') return [anchor];
    if (range === 'week') {
      const start = startOfWeek(anchor);
      return Array.from({ length: 7 }, (_, i) => addDays(start, i));
    }
    const y = anchor.getFullYear();
    const m = anchor.getMonth();
    const first = new Date(y, m, 1);
    const startD = startOfWeek(first);
    return Array.from({ length: 42 }, (_, i) => addDays(startD, i));
  }, [range, anchor]);

  // Map tasks to dates — expand recurring tasks across the visible range
  const tasksByDate = useMemo(() => {
    const map = new Map<string, TaskingBotTask[]>();
    const rangeStart = dates[0];
    const rangeEnd = dates[dates.length - 1];

    // Debug: log tasks with cron expressions
    const cronTasks = allTasks.filter(t => t.cronExpression);
    if (cronTasks.length > 0) {
      console.log('[TaskSchedule] Tasks with cron:', cronTasks.map(t => ({
        id: t.id, title: t.title, cron: t.cronExpression, status: t.status,
      })));
    }

    for (const task of allTasks) {
      // Recurring tasks: expand cron expression across visible date range
      if (task.cronExpression && task.status !== 'completed' && task.status !== 'cancelled') {
        const cronDates = getCronDates(task.cronExpression, rangeStart, rangeEnd);
        console.log('[TaskSchedule] Cron expanded:', task.title, task.cronExpression, '→', cronDates.length, 'dates in range', formatDate(rangeStart), '-', formatDate(rangeEnd));
        for (const key of cronDates) {
          if (!map.has(key)) map.set(key, []);
          // Avoid duplicates
          if (!map.get(key)!.some(t => t.id === task.id)) {
            map.get(key)!.push(task);
          }
        }
        continue; // Don't also add by single date
      }

      // Non-recurring: single date
      const dateStr = getTaskDate(task);
      if (!dateStr) continue;
      const key = dateStr.slice(0, 10);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(task);
    }
    return map;
  }, [allTasks, dates]);

  const undatedTasks = useMemo(
    () => allTasks.filter(t => !getTaskDate(t) && !t.cronExpression),
    [allTasks],
  );

  const nav = (dir: -1 | 1) => {
    if (range === 'day') setAnchor(addDays(anchor, dir));
    else if (range === 'week') setAnchor(addDays(anchor, dir * 7));
    else setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + dir, 1));
  };

  const headerLabel = range === 'day'
    ? anchor.toLocaleDateString('en', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
    : range === 'week'
      ? `${dates[0].toLocaleDateString('en', { month: 'short', day: 'numeric' })} – ${dates[6].toLocaleDateString('en', { month: 'short', day: 'numeric', year: 'numeric' })}`
      : anchor.toLocaleDateString('en', { month: 'long', year: 'numeric' });

  const isCurrentMonth = (d: Date) => d.getMonth() === anchor.getMonth();

  // Stats — include both dated and cron tasks
  const totalScheduled = allTasks.filter(t => getTaskDate(t) || t.cronExpression).length;
  const cronCount = allTasks.filter(t => t.cronExpression && t.status !== 'completed' && t.status !== 'cancelled').length;
  const dueToday = tasksByDate.get(todayStr)?.length || 0;
  const overdue = allTasks.filter(t => {
    if (t.cronExpression) return false; // cron tasks don't become "overdue"
    const d = getTaskDate(t);
    return d && d.slice(0, 10) < todayStr && t.status !== 'completed' && t.status !== 'cancelled';
  }).length;

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading schedule…</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Stats row */}
      <div className={styles.statsRow}>
        <div className={styles.stat}>
          <span className={styles.statNum}>{allTasks.length}</span>
          <span className={styles.statLabel}>Total</span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statNum}>{totalScheduled}</span>
          <span className={styles.statLabel}>Scheduled</span>
        </div>
        {cronCount > 0 && (
          <div className={styles.stat}>
            <span className={styles.statNum} style={{ color: '#8b5cf6' }}>{cronCount}</span>
            <span className={styles.statLabel}>🔁 Recurring</span>
          </div>
        )}
        <div className={styles.stat}>
          <span className={styles.statNum} style={{ color: dueToday > 0 ? '#f59e0b' : undefined }}>{dueToday}</span>
          <span className={styles.statLabel}>Due today</span>
        </div>
        {overdue > 0 && (
          <div className={styles.stat}>
            <span className={styles.statNum} style={{ color: '#ef4444' }}>{overdue}</span>
            <span className={styles.statLabel}>Overdue</span>
          </div>
        )}
        <div className={styles.stat}>
          <span className={styles.statNum}>{undatedTasks.length}</span>
          <span className={styles.statLabel}>Undated</span>
        </div>
      </div>

      {/* Navigation header */}
      <div className={styles.header}>
        <div className={styles.nav}>
          <button className={styles.navBtn} onClick={() => nav(-1)}><ChevronLeft size={14} /></button>
          <span className={styles.label}>{headerLabel}</span>
          <button className={styles.navBtn} onClick={() => nav(1)}><ChevronRight size={14} /></button>
          <button className={styles.todayBtn} onClick={() => setAnchor(new Date())}>Today</button>
        </div>
        <div className={styles.rangeToggle}>
          {(['day', 'week', 'month'] as ScheduleRange[]).map(r => (
            <button
              key={r}
              className={`${styles.rangeBtn} ${range === r ? styles.rangeBtnActive : ''}`}
              onClick={() => setRange(r)}
            >
              {r.charAt(0).toUpperCase() + r.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Day view */}
      {range === 'day' && (
        <div className={styles.dayView}>
          {(() => {
            const dayTasks = tasksByDate.get(formatDate(anchor)) || [];
            return dayTasks.length === 0 ? (
              <div className={styles.empty}>No tasks scheduled for this day.</div>
            ) : (
              <div className={styles.dayTasks}>
                {dayTasks.map(task => {
                  const badge = STATUS_BADGE[task.status] || STATUS_BADGE.open;
                  const project = task.projectId ? projectMap.get(task.projectId) : undefined;
                  const taskTime = getTaskTime(task);
                  return (
                    <div key={task.id} className={styles.taskRow} onClick={() => setDetailTaskId(task.id)}>
                      <span className={styles.taskDot} style={{ background: badge.color }} />
                      {taskTime && (
                        <span style={{ fontSize: 11, fontWeight: 600, color: badge.color, fontFamily: 'JetBrains Mono, monospace', minWidth: '70px' }}>
                          {taskTime}
                        </span>
                      )}
                      {task.cronExpression && <span style={{ fontSize: 9, opacity: 0.6 }}>🔁</span>}
                      <span className={styles.taskTitle}>{task.title}</span>
                      {project && <ProjectTag project={project} size="xs" />}
                      {task.priority && (
                        <span style={{ color: PRIORITY_COLORS[task.priority], fontSize: 9, fontWeight: 700 }}>
                          {PRIORITY_LABELS[task.priority]}
                        </span>
                      )}
                      <span className={styles.taskBadge} style={{ color: badge.color, background: badge.bg, marginLeft: 'auto' }}>
                        {badge.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
      )}

      {/* Week view */}
      {range === 'week' && (
        <div className={styles.weekGrid}>
          {dates.map(date => {
            const key = formatDate(date);
            const dayTasks = tasksByDate.get(key) || [];
            const isToday = key === todayStr;
            return (
              <div key={key} className={`${styles.weekDay} ${isToday ? styles.weekDayToday : ''}`}>
                <div className={styles.weekDayHeader}>
                  <span className={styles.weekDayName}>{date.toLocaleDateString('en', { weekday: 'short' })}</span>
                  <span className={`${styles.weekDayNum} ${isToday ? styles.weekDayNumToday : ''}`}>
                    {date.getDate()}
                  </span>
                </div>
                <div className={styles.weekDayBody}>
                  {dayTasks
                    .slice()
                    .sort((a, b) => {
                      const ta = getTaskTime(a) || 'zzz';
                      const tb = getTaskTime(b) || 'zzz';
                      return ta.localeCompare(tb);
                    })
                    .map(task => {
                      const badge = STATUS_BADGE[task.status] || STATUS_BADGE.open;
                      const taskTime = getTaskTime(task);
                      return (
                        <div key={task.id} className={styles.weekTask} onClick={() => setDetailTaskId(task.id)} style={{ borderLeftColor: badge.color }}>
                          {taskTime && (
                            <div style={{ fontSize: 10, fontWeight: 700, color: badge.color, marginBottom: 2 }}>
                              {taskTime}
                            </div>
                          )}
                          <span className={styles.weekTaskTitle}>{task.title}</span>
                        </div>
                      );
                    })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Month view */}
      {range === 'month' && (
        <>
          <div className={styles.monthDayNames}>
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
              <span key={d} className={styles.monthDayName}>{d}</span>
            ))}
          </div>
          <div className={styles.monthGrid}>
            {dates.map(date => {
              const key = formatDate(date);
              const dayTasks = tasksByDate.get(key) || [];
              const isToday = key === todayStr;
              const inMonth = isCurrentMonth(date);
              return (
                <div key={key} className={`${styles.monthCell} ${!inMonth ? styles.monthCellDim : ''} ${isToday ? styles.monthCellToday : ''}`}>
                  <span className={styles.monthCellNum}>{date.getDate()}</span>
                  {dayTasks.slice(0, 3).map(task => {
                    const badge = STATUS_BADGE[task.status] || STATUS_BADGE.open;
                    const taskTime = getTaskTime(task);
                    return (
                      <div key={task.id} className={styles.monthTask} onClick={() => setDetailTaskId(task.id)} style={{ background: badge.bg, color: badge.color }} title={taskTime ? `${taskTime} — ${task.title}` : task.title}>
                        {taskTime && <span style={{ fontWeight: 700, marginRight: 4 }}>{taskTime}</span>}
                        {task.title}
                      </div>
                    );
                  })}
                  {dayTasks.length > 3 && (
                    <span className={styles.monthMore}>+{dayTasks.length - 3} more</span>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Undated tasks */}
      {undatedTasks.length > 0 && (
        <div className={styles.undated}>
          <span className={styles.undatedLabel}>No date ({undatedTasks.length})</span>
          <div className={styles.undatedList}>
            {undatedTasks.slice(0, 10).map(task => {
              const badge = STATUS_BADGE[task.status] || STATUS_BADGE.open;
              return (
                <div key={task.id} className={styles.taskRow} onClick={() => setDetailTaskId(task.id)}>
                  <span className={styles.taskDot} style={{ background: badge.color }} />
                  <span className={styles.taskTitle}>{task.title}</span>
                </div>
              );
            })}
            {undatedTasks.length > 10 && <span className={styles.monthMore}>+{undatedTasks.length - 10} more</span>}
          </div>
        </div>
      )}

      {/* Task detail modal */}
      {detailTaskId && (
        <TaskDetailModal
          taskId={detailTaskId}
          open={!!detailTaskId}
          onOpenChange={(open) => { if (!open) setDetailTaskId(null); }}
        />
      )}
    </div>
  );
}
